// Hooks da API por domínio (régua de 400 linhas): este arquivo mantém o ponto
// de import único — os domínios maiores vivem nos hooks-*.ts re-exportados.
export * from './hooks-house-people';
export * from './hooks-events-tasks';
export * from './hooks-inventory-finance';
export * from './hooks-billing';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  obligationsApi,
  reportsApi,
  documentsApi,
  rolesApi,
  functionsApi,
  communicationApi,
} from './endpoints';
import { getSession } from '../auth/session';

export function useObligations(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ['obligations', params],
    queryFn: () => obligationsApi.list(params),
  });
}

export function useObligation(id: string) {
  return useQuery({
    queryKey: ['obligations', id],
    queryFn: () => obligationsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => obligationsApi.create(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['obligations'] });
    },
  });
}

export function useUpdateObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: unknown }) => obligationsApi.update(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['obligations'] });
    },
  });
}

export function useChangeObligationStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: unknown }) =>
      obligationsApi.changeStatus(id, status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['obligations'] });
    },
  });
}

export function useAddResponsible() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, personId }: { id: string; personId: string }) =>
      obligationsApi.addResponsible(id, personId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['obligations'] });
    },
  });
}

export function useRemoveResponsible() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, responsibleId }: { id: string; responsibleId: string }) =>
      obligationsApi.removeResponsible(id, responsibleId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['obligations'] });
    },
  });
}

export function useDashboard() {
  return useQuery({ queryKey: ['reports', 'dashboard'], queryFn: () => reportsApi.getDashboard() });
}

export function useFinanceReport() {
  return useQuery({
    queryKey: ['reports', 'finance'],
    queryFn: () => reportsApi.getFinanceSummary(),
  });
}

export function useRoles() {
  return useQuery({ queryKey: ['roles'], queryFn: () => rolesApi.list() });
}

export function useRole(id: string) {
  return useQuery({ queryKey: ['roles', id], queryFn: () => rolesApi.getById(id), enabled: !!id });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => rolesApi.create(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: unknown }) => rolesApi.update(id, data),
    onSuccess: () => {
      // Role (digital cargo) name appears on people and assignment surfaces
      void qc.invalidateQueries({ queryKey: ['roles'] });
      void qc.invalidateQueries({ queryKey: ['people'] });
      void qc.invalidateQueries({ queryKey: ['permissions'] });
    },
  });
}

export function useSetRolePermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, permissions }: { id: string; permissions: string[] }) =>
      rolesApi.setPermissions(id, permissions),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

export function useAssignRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, personId }: { roleId: string; personId: string }) =>
      rolesApi.assign(roleId, personId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['roles'] });
      void qc.invalidateQueries({ queryKey: ['people'] });
      void qc.invalidateQueries({ queryKey: ['permissions'] });
    },
  });
}

export function useRemoveRoleAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, assignmentId }: { roleId: string; assignmentId: string }) =>
      rolesApi.removeAssignment(roleId, assignmentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['roles'] });
      void qc.invalidateQueries({ queryKey: ['people'] });
      void qc.invalidateQueries({ queryKey: ['permissions'] });
    },
  });
}

export function useMyPermissions() {
  return useQuery({
    queryKey: ['permissions', 'effective', getSession()?.houseId ?? null],
    queryFn: () => rolesApi.getMyPermissions(),
  });
}

export function useFunctions(params?: Record<string, unknown>) {
  return useQuery({ queryKey: ['functions', params], queryFn: () => functionsApi.list(params) });
}

export function useFunction(id: string) {
  return useQuery({
    queryKey: ['functions', id],
    queryFn: () => functionsApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateFunction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => functionsApi.create(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['functions'] });
    },
  });
}

export function useUpdateFunction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: unknown }) => functionsApi.update(id, data),
    onSuccess: () => {
      // Religious/admin function name appears on person detail and lists
      void qc.invalidateQueries({ queryKey: ['functions'] });
      void qc.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

export function useAssignFunction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, personId }: { id: string; personId: string }) =>
      functionsApi.assign(id, personId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['functions'] });
      void qc.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

export function useUnassignFunction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, assignmentId }: { id: string; assignmentId: string }) =>
      functionsApi.unassign(id, assignmentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['functions'] });
      void qc.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

export function useDocuments(params?: Record<string, unknown>) {
  return useQuery({ queryKey: ['documents', params], queryFn: () => documentsApi.list(params) });
}

export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) => documentsApi.upload(formData),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export function useArchiveDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => documentsApi.archive(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export function useDownloadDocument() {
  return useMutation({
    mutationFn: (id: string) => documentsApi.download(id),
  });
}

export function useDocument(id: string) {
  return useQuery({
    queryKey: ['documents', id],
    queryFn: () => documentsApi.get(id),
    enabled: !!id,
  });
}

export function useUpdateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: unknown }) => documentsApi.update(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export function useMessages(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ['communication', 'messages', params],
    queryFn: () => communicationApi.listMessages(params),
  });
}

export function useCreateMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => communicationApi.createMessage(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['communication'] });
    },
  });
}
